import { revalidateInvestigatorTargetsAction } from './investigator-revalidation-actions';
import {
	isValidInvestigatorProfileSlug,
	type InvestigatorRevalidationDescriptor,
} from './investigator-profile-cache';

// ─── Client-facing revalidation helpers ─────────────────────────────────────
//
// Thin wrappers that client components call after a successful mutation. They
// delegate to the Server Action (which runs on the Next server, bypassing the
// Apache `/api/ -> NestJS` collision). All best-effort: a failure logs a warning
// and returns false; it never blocks or reverts the mutation.

/**
 * Unified entry point: "revalidate THESE targets". Invalid slugs are dropped.
 */
export async function requestInvestigatorRevalidation(
	descriptor: InvestigatorRevalidationDescriptor,
): Promise<boolean> {
	const profiles = (descriptor.profiles ?? []).filter((slug) => isValidInvestigatorProfileSlug(slug));

	try {
		const result = await revalidateInvestigatorTargetsAction({ ...descriptor, profiles });
		return result.ok;
	} catch (error) {
		console.warn('[investigator-revalidation] Could not revalidate cache', { descriptor, error });
		return false;
	}
}

/**
 * Revalidate a profile after a section mutation. When a rename changed the slug,
 * pass `previousSlug` so the OLD profile page/link is invalidated too. Also busts
 * the directory listing (the card in `/investigadores` picks up the new
 * name/slug) and the owner dashboard.
 */
export async function revalidateInvestigatorProfileBestEffort(
	slug: string,
	options: { previousSlug?: string | null } = {},
) {
	if (!isValidInvestigatorProfileSlug(slug)) {
		console.warn('[investigator-revalidation] Skipping revalidation: invalid slug', { slug });
		return false;
	}

	const safeSlug = slug.trim();
	const previousSlug = options.previousSlug?.trim();
	const validPreviousSlug =
		previousSlug && previousSlug !== safeSlug && isValidInvestigatorProfileSlug(previousSlug)
			? previousSlug
			: undefined;

	if (previousSlug && previousSlug !== safeSlug && !validPreviousSlug) {
		console.warn('[investigator-revalidation] Skipping previous slug revalidation: invalid slug', { previousSlug });
	}

	return requestInvestigatorRevalidation({
		profiles: validPreviousSlug ? [safeSlug, validPreviousSlug] : [safeSlug],
		directory: true,
		miPerfil: true,
	});
}

/**
 * Revalidate ONLY the public directory/home listing cache (`/investigadores`, `/`).
 *
 * Use after mutations that change who appears in the directory but not a
 * specific cached profile page — e.g. right after a brand-new researcher
 * completes onboarding.
 */
export async function revalidateInvestigatorDirectoryBestEffort() {
	return requestInvestigatorRevalidation({ directory: true });
}

/**
 * Run a profile mutation and then trigger public profile/export revalidation.
 *
 * Use this wrapper after any successful profile section mutation (PUT/POST/DELETE)
 * that affects `/investigadores/[slug]` or `/investigadores/[slug]/exportar`.
 * Revalidation is best-effort: mutation success is never reverted by cache failures.
 */
export async function runWithInvestigatorProfileRevalidation<T>(
	slug: string,
	mutation: () => Promise<T>,
) {
	const result = await mutation();
	await revalidateInvestigatorProfileBestEffort(slug);
	return result;
}
